import { CreateControllerFn } from '@wix/yoshi-flow-editor';
import type { ControllerProps, Category } from './context';
import {
  VSearchCategoryApiClient,
  AdiCategorySearchService,
  onLoadCallback,
  CategoryResult,
} from '@wix/category-search-component/services';
import { fetchUserId } from './utils/fetchUserId';
import { HttpClient } from '@wix/http-client';
import { shuffle } from 'lodash';
import { BNGBiReporter } from './utils/BNGBiReporter';

const biReporter = new BNGBiReporter();

const buildCategory = (category: CategoryResult): Category => {
  return {
    value: category.displayName ?? '',
    postRegVertical: category.postRegVertical ?? '',
    industryId: category.industryId ?? '',
    structureId: category.structureId ?? '',
  };
};

const createController: CreateControllerFn = async ({
  controllerConfig,
  flowAPI,
}) => {
  const httpClient = new HttpClient();
  const AI_PROMPT_MAX_RETRY = 5;
  const AI_PROMPT_KEY = 'businessNameGenerator';
  const userLoginAttempt = await fetchUserId(
    flowAPI.httpClient,
    flowAPI.errorMonitor,
  );
  const { setProps, wixCodeApi } = controllerConfig;
  const onCategoriesLoad: onLoadCallback = (result) => {
    setProps({
      categories: result.map(buildCategory),
    });
  };
  const vSearchApiClient = new VSearchCategoryApiClient();
  const searchCategoryService = new AdiCategorySearchService(
    vSearchApiClient,
    onCategoriesLoad,
  );

  const updateUrlParams = (newUrlParams: { [Param: string]: string }) => {
    wixCodeApi.location.queryParams.remove(Object.keys(newUrlParams));
    wixCodeApi.location.queryParams.add(newUrlParams);
  };

  const initialBusinessIdea =
    wixCodeApi.location.query?.businessIdea ?? 'My Business Name';
  const industryName = wixCodeApi.location.query?.industry ?? '';
  const searchResult = await vSearchApiClient.suggest(industryName);
  const initialIndustry = searchResult.find(
    (category) => category.displayName === industryName,
  );

  const onBngResponseCallback = (data: Partial<ControllerProps>) => {
    const { businessIdea, industry } = data;
    if (businessIdea) {
      updateUrlParams({
        businessIdea,
      });
    }
    if (industry?.value) {
      updateUrlParams({
        industry: industry.value,
      });
    }

    setProps(data);
  };

  const generateNames = async (
    keyword: string,
    category: Category,
    language: string,
  ): Promise<void> => {
    const response = await generateAISuggestions(
      keyword,
      category.value,
      language,
    );
    onBngResponseCallback({
      businessIdea: keyword,
      industry: category,
      bngRequestId: response?.requestId,
      namesList: response?.suggestions,
    });
  };

  const getAIBusinessName = (
    industry: string | undefined,
    keywords: string | undefined,
    language: string,
  ) => {
    return httpClient
      .post('/_serverless/brand-maker/api/v1/ai/bng/suggestBusinessName', {
        industry,
        keywords,
        language,
      })
      .then((response: { data: any; requestId: string }) => {
        const { data, requestId } = response;
        return { data, requestId };
      });
  };

  const generateAISuggestions = async (
    keywords: string,
    industry: string,
    language: string,
    retry: number = 0,
  ) => {
    const query = wixCodeApi.location.url;
    if (retry === AI_PROMPT_MAX_RETRY) {
      biReporter.reportBNGOpenaiApiCallAttempt({ keywords, industry }, '1', [], retry, 'fail', AI_PROMPT_KEY, 'BNG', query);
      throw new Error(
        "generateAIBusinessNamePrompt - couldn't parse over returned prompt after retry",
      );
    }

    const promptIndustry = industry === '' ? 'Local Business' : industry;
    const promptResults = await getAIBusinessName(
      promptIndustry,
      keywords,
      language,
    );

    const regex = /(["\n])([\p{L}\p{N}_ $%&@#*!]+)"/gu;
    const matches = promptResults.data.contents.matchAll(regex);
    const suggestions = [];
    for (const match of matches) {
      suggestions.push(match[2]);
    }
    if (!suggestions.length) {
      const backupRegex = /\d+\.(.+)/g;
      const backupMatches = promptResults.data.contents.matchAll(backupRegex);
      for (const match of backupMatches) {
        suggestions.push(match[1].trim());
      }
    }

    if (
      !suggestions.length ||
      suggestions.length < 6 ||
      suggestions.find((name) => name.endsWith(':'))
    ) {
      generateAISuggestions(keywords, industry, language, retry + 1);
    } else {
      biReporter.reportBNGOpenaiApiCallAttempt({ keywords, industry }, '1', suggestions, retry, 'success', AI_PROMPT_KEY, 'BNG', query);
      const shuffledSuggestions = shuffle(suggestions);
      return {
        suggestions: shuffledSuggestions,
        requestId: promptResults.requestId,
      };
    }
  };

  return {
    pageReady: () => {
      setProps({
        isLoggedIn: userLoginAttempt.success,
        getAppToken: () => controllerConfig.appParams.instance,
        bngRequestId: '',
        isMobile: flowAPI.environment.isMobile,
        containerId: controllerConfig.compId,
        businessIdea: initialBusinessIdea,
        industry: initialIndustry
          ? buildCategory(initialIndustry)
          : { value: '' },
        searchCategories: (term) => searchCategoryService.suggest(term),
        categories: searchResult.map(buildCategory),
        namesList: [],
        generateNames,
        onBngResponseCallback,
        isSSR: flowAPI.environment.isSSR,
      } as Partial<ControllerProps>);
    },
  };
};

export default createController;
